import {Formik} from 'formik';
import {FormWithButtons} from '@flexinet/ui-components';

import {FormComponent} from './FormComponent';
import {RecordSchema} from './RecordSchema';
import {ConnectFormValues} from './types';
import {DEFAULT_GRANTS} from '../services/rolePolicyBuilder';

// FNP-1248 — the CONNECT form wrapper.
//
// ⚠️ NAVIGATION-FREE, per the drawer pattern. No `useNavigate` and no
// `useParams`: the form lives inside a drawer, and the drawer decides what
// happens after a submit. A `navigate()` in here would fight the drawer — and
// in this module it would be actively harmful, because the drawer deliberately
// STAYS OPEN after a successful connect in order to show the external id.

export const RecordForm = (
    {
        handleSubmit,
        handleCancel,
    }: {
        // Returns a promise so the drawer can await the real request — Formik
        // also uses it to decide when `isSubmitting` ends, so a void signature
        // would drop the spinner the instant the button was clicked.
        handleSubmit: (values: ConnectFormValues) => Promise<unknown>,
        handleCancel?: () => void,
    },
) => {

    // RETURNED, not fire-and-forget. Formik awaits what onSubmit returns, which
    // keeps `isSubmitting` true for the length of the real request.
    const onSubmit = (values: ConnectFormValues) => handleSubmit(values);

    // Wrapped rather than passed through: FormWithButtons types its cancel prop
    // as a MouseEventHandler, which is not optional, so an undefined
    // `handleCancel` cannot be forwarded directly.
    const onCancel = () => {
        handleCancel?.();
    };

    // ⚠️ `writable` DEFAULTS TO FALSE. FNP-1233's non-negotiable: a connection
    // nobody deliberately marked writable must not be able to write. The
    // default lives here, in one place, rather than being implied by an
    // uncontrolled switch.
    //
    // ⚠️ There is deliberately no `record` prop and no prefill. This form only
    // ever CREATES — a connection's credential is changed through the rotate
    // path, which has its own form, because rotating cannot change the provider
    // or the display name.
    const initialValues: ConnectFormValues = {
        providerId: '',
        displayName: '',
        credentialType: '',
        roleArn: '',
        apiToken: '',
        writable: false,
        // ⚠️ FNP-1363 — the starting ticks. Read is always on because a role
        // that cannot list zones fails connect-time validation, so it is not a
        // real choice. Record writes and certificates start ON because they are
        // what most tenants come here to do, and zone lifecycle starts OFF
        // because creating and deleting zones is the most destructive grant on
        // the list and should be a deliberate act.
        //
        // A tenant who wants less has "Start read-only" in one click.
        grants: DEFAULT_GRANTS,
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={RecordSchema}
            onSubmit={onSubmit}
        >
            {({
                  handleSubmit: formikHandleSubmit,
                  isSubmitting,
                  ...rest
              }) => (
                <FormWithButtons
                    handleSubmit={formikHandleSubmit}
                    isSubmitting={isSubmitting}
                    handleCancel={onCancel}
                    // ⚠️ HARD-WIRED FALSE, NOT `stage === 'ide'`.
                    //
                    // The zones form passes `false && stage === 'ide'`, which is
                    // the same value by a longer route. Here it must never be
                    // anything else: DisplayFormikState renders the entire
                    // values object, and this form's values object contains
                    // `apiToken`. A debug panel would print a live bearer token
                    // onto the screen in whatever environment enabled it.
                    debug={false}
                    {...rest}
                >
                    <FormComponent/>
                </FormWithButtons>
            )}
        </Formik>
    );
};
